import { useEffect, useState, useRef, useCallback } from 'react';

interface TelemetryUpdate {
  symbol: string;
  price: number;
  timestamp: string;
  change: number;
  change_percent: number;
}

export const useMarketStream = (symbols: string[]) => {
  const [telemetry, setTelemetry] = useState<Record<string, TelemetryUpdate>>({});
  const [isConnected, setIsConnected] = useState(false);
  const ws = useRef<WebSocket | null>(null);
  const retryCount = useRef(0);
  const retryTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const MAX_RETRIES = 10;
  const BASE_DELAY_MS = 1000;

  const connect = useCallback(() => {
    const wsUrl = import.meta.env.VITE_WS_URL || 'ws://localhost:8000/ws';

    try {
      ws.current = new WebSocket(wsUrl);
    } catch {
      return; // Invalid URL — abort silently
    }

    ws.current.onopen = () => {
      retryCount.current = 0; // Reset backoff on success
      setIsConnected(true);
      ws.current?.send(JSON.stringify({
        action: 'subscribe',
        symbols: symbols
      }));
    };

    ws.current.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'TELEMETRY_UPDATE') {
          (window as any).lastTelemetryFrame = data;
          setTelemetry(prev => ({
            ...prev,
            [data.symbol]: {
              symbol: data.symbol,
              price: data.price,
              timestamp: data.timestamp,
              change: data.change,
              change_percent: data.change_percent
            }
          }));
        }
      } catch {
        // Suppress parse errors
      }
    };

    ws.current.onerror = () => {
      // Suppress — onclose fires next and handles retry
    };

    ws.current.onclose = () => {
      setIsConnected(false);
      if (retryCount.current >= MAX_RETRIES) {
        console.warn(`[MarketStream] Max retries reached. Stream paused.`);
        return;
      }
      const delay = Math.min(BASE_DELAY_MS * 2 ** retryCount.current, 60_000);
      retryCount.current++;
      if (retryTimer.current) clearTimeout(retryTimer.current);
      retryTimer.current = setTimeout(connect, delay);
    };
  }, [symbols]);

  useEffect(() => {
    connect();
    return () => {
      ws.current?.close();
    };
  }, [connect]);

  return { telemetry, isConnected };
};
